import React, {
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from 'react';

import { JarabotSurface } from './types';

interface JarabotContextOwnership {
	claim: (surface: JarabotSurface) => boolean;
	release: (surface: JarabotSurface) => void;
	releaseCount: number;
}

const OwnershipContext = createContext<JarabotContextOwnership>({
	claim: () => true,
	release: () => undefined,
	releaseCount: 0,
});

export function JarabotContextOwnershipProvider({
	children,
}: {
	children: ReactNode;
}): JSX.Element {
	const ownerRef = useRef<JarabotSurface | null>(null);
	const [releaseCount, countRelease] = useReducer((count: number) => count + 1, 0);
	const handlers = useMemo(
		() => ({
			claim: (surface: JarabotSurface): boolean => {
				if (ownerRef.current !== null && ownerRef.current !== surface) return false;
				ownerRef.current = surface;
				return true;
			},
			release: (surface: JarabotSurface): void => {
				if (ownerRef.current !== surface) return;
				ownerRef.current = null;
				countRelease();
			},
		}),
		[]
	);
	const ownership = useMemo<JarabotContextOwnership>(
		() => ({ ...handlers, releaseCount }),
		[handlers, releaseCount]
	);

	return <OwnershipContext.Provider value={ownership}>{children}</OwnershipContext.Provider>;
}

export function useJarabotContextOwnership(surface: JarabotSurface, isActive: boolean): boolean {
	const { claim, release, releaseCount } = useContext(OwnershipContext);
	const [hasClaimed, setHasClaimed] = useState(false);

	useEffect(() => {
		if (hasClaimed || !isActive || !claim(surface)) return;

		setHasClaimed(true);
	}, [claim, hasClaimed, isActive, releaseCount, surface]);

	useEffect(() => {
		if (!hasClaimed || !isActive) return undefined;

		return () => {
			release(surface);
			setHasClaimed(false);
		};
	}, [hasClaimed, isActive, release, surface]);

	return hasClaimed && isActive;
}
